import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";

import { Button, Card } from "@/design-system/primitives";
import { ArrowInline } from "@/lib/Icon";
import {
  useAwaitingOrderPreview,
  useAwaitingOrders,
  useReleaseAwaitingOrderHold,
  useSubmitAwaitingOrder,
} from "@/lib/api/adminHooks";
import type {
  AwaitingOrderLinePreview,
  AwaitingOrderPreviewResponse,
  AwaitingOrderRow,
  AwaitingOrderSubmitResult,
} from "@/lib/api/adminHooks";

/**
 * Detail page for the awaiting-prescription queue (eRx-first lander flow).
 *
 * Each row represents a `pharmacy_orders` row in `awaiting_prescription`
 * — the patient has paid on the lander but the clinician hasn't written
 * the eRx yet. When the eRx lands in Healthie, the prescription.created
 * webhook normally fires submission automatically; while
 * `auto_submit_enabled` is OFF, admins trigger submission from here.
 */
export function AwaitingOrdersScreen() {
  const [query, setQuery] = useState("");
  const [heldOnly, setHeldOnly] = useState(false);
  // 3+ characters search every awaiting order on the server; the loaded list
  // is only the held orders plus the newest unheld ones.
  const [serverSearch, setServerSearch] = useState("");
  useEffect(() => {
    const term = query.trim();
    const timer = setTimeout(
      () => setServerSearch(term.length >= 3 ? term : ""),
      300,
    );
    return () => clearTimeout(timer);
  }, [query]);
  const { data, isLoading, isError, error } = useAwaitingOrders(serverSearch);

  const orders = data?.orders ?? [];
  const searched = useMemo(
    () => (data?.query ? orders : filterOrders(orders, query)),
    [orders, query, data?.query],
  );
  const filtered = useMemo(
    () => (heldOnly ? searched.filter((o) => o.fulfillment_hold) : searched),
    [searched, heldOnly],
  );
  const heldCount =
    data?.held_total ?? orders.filter((o) => o.fulfillment_hold).length;

  return (
    <div className="flex flex-col gap-24">
      <Header
        autoSubmit={data?.auto_submit_enabled}
        total={data?.total_awaiting ?? orders.length}
        shown={orders.length}
        searching={Boolean(data?.query)}
      />

      <SearchBar value={query} onChange={setQuery} />
      {data?.truncated && (
        <p className="type-body-sm text-ink-muted">
          More orders match than are shown. Refine your search, e.g. the full
          email or order number.
        </p>
      )}

      <div className="flex items-center gap-8 flex-wrap">
        <Button
          size="sm"
          variant={heldOnly ? "secondary" : "primary"}
          aria-pressed={!heldOnly}
          onClick={() => setHeldOnly(false)}
        >
          All
        </Button>
        <Button
          size="sm"
          variant={heldOnly ? "primary" : "secondary"}
          aria-pressed={heldOnly}
          onClick={() => setHeldOnly(true)}
        >
          Held for review ({heldCount})
        </Button>
      </div>

      {isLoading ? (
        <p className="type-body-sm text-ink-muted">Loading…</p>
      ) : isError ? (
        <p className="type-body-sm text-status-danger">
          Failed to load: {String((error as Error)?.message || error)}
        </p>
      ) : orders.length === 0 && !data?.query ? (
        <EmptyState />
      ) : filtered.length === 0 ? (
        <p className="type-body-sm text-ink-muted">
          {heldOnly
            ? query.trim()
              ? `No held orders match "${query}".`
              : "No held orders."
            : `No awaiting orders match "${query}".`}
        </p>
      ) : (
        <div className="flex flex-col gap-12">
          {filtered.map((o) => (
            <AwaitingOrderCard key={o.id} order={o} />
          ))}
        </div>
      )}
    </div>
  );
}

function Header({
  autoSubmit,
  total,
  shown,
  searching,
}: {
  autoSubmit?: boolean;
  total: number;
  shown: number;
  searching: boolean;
}) {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-baseline justify-between flex-wrap gap-10">
        <h1 className="type-heading-xl">Awaiting prescription</h1>
        <Link
          to="/admin/clinical-ops"
          className="type-body-sm text-accent hover:underline"
        >
          ← Clinical Ops
        </Link>
      </div>
      <p className="type-body-md text-ink-secondary max-w-prose">
        Orders here have been paid for on the lander but are waiting on a
        clinician-written eRx before the paired API fill can be submitted to the
        pharmacy. {total} order{total === 1 ? "" : "s"} pending.
        {!searching && shown < total && (
          <>
            {" "}
            Showing every order held for review plus the newest others; search
            finds any of them.
          </>
        )}
      </p>
      {autoSubmit === false && (
        <div className=" border border-status-warning bg-status-warning-subtle px-15 py-12">
          <div className="type-label-sm uppercase tracking-tight text-status-warning">
            Auto-submit OFF
          </div>
          <div className="type-body-sm text-ink-secondary mt-4">
            <code>PPI_AUTO_SUBMIT_PAIRED_FILL</code> is disabled. New eRx will
            not automatically trigger pharmacy submission — use the Submit
            button on each row.
          </div>
        </div>
      )}
      {autoSubmit === true && (
        <div className=" border border-status-success bg-status-success-subtle px-15 py-12">
          <div className="type-label-sm uppercase tracking-tight text-status-success">
            Auto-submit ON
          </div>
          <div className="type-body-sm text-ink-secondary mt-4">
            New eRx will automatically trigger pharmacy submission when the
            Healthie webhook lands. Manual submission below is only needed for
            retries or stuck orders.
          </div>
        </div>
      )}
    </div>
  );
}

function SearchBar({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search patient name, email, product, order number…"
        className="w-full border border-line-default px-15 py-10 type-body-md"
      />
    </div>
  );
}

function EmptyState() {
  return (
    <div className=" border border-line-subtle bg-surface-subtle px-15 py-24 text-center">
      <div className="type-title-sm">All caught up</div>
      <div className="type-body-sm text-ink-muted mt-6">
        No orders are currently awaiting a clinician-written eRx.
      </div>
    </div>
  );
}

function AwaitingOrderCard({ order }: { order: AwaitingOrderRow }) {
  const submit = useSubmitAwaitingOrder();
  const releaseHold = useReleaseAwaitingOrderHold();
  const [result, setResult] = useState<AwaitingOrderSubmitResult | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [releaseReason, setReleaseReason] = useState("");
  const [releaseId] = useState(() => crypto.randomUUID());
  const preview = useAwaitingOrderPreview(showDetails ? order.id : null);
  const created = order.created_at
    ? format(parseISO(order.created_at), "MMM d yyyy, h:mm a")
    : "—";

  const isTph = order.partner_key === "the_pharmacy_hub";
  const partnerName = isTph
    ? "The Pharmacy Hub (BondRx)"
    : "Precision Compounding Pharmacy";
  const reconciliationPending =
    result?.status === "submitted_reconciliation_pending";
  const releaseAttemptRecorded = Boolean(
    result && result.status !== "error" && !reconciliationPending,
  );

  async function handleSubmit() {
    if (
      !window.confirm(
        `Submit awaiting order to pharmacy now?\n\n` +
          `Patient: ${order.patient_display_name ?? order.patient_email ?? order.patient_id}\n` +
          `Product: ${order.product_id ?? "unknown"}\n` +
          `Partner: ${partnerName}\n\n` +
          (isTph
            ? `This will render the Rx PDF and transmit to BondRx (sandbox/prod per env). The order needs the medication added in Healthie for clinical approval.`
            : `This will fire the paired API call to PPI if the eRx is on file in Healthie.`),
      )
    ) {
      return;
    }
    try {
      const r = await submit.mutateAsync({ orderId: order.id });
      setResult(r);
    } catch (e) {
      setResult({
        pharmacy_order_id: null,
        status: "error",
        partner_key: null,
        partner_order_id: null,
        error: String((e as Error)?.message || e),
      });
    }
  }

  async function handleReleaseHold() {
    const reason = releaseReason.trim();
    if (reason.length < 8) return;
    if (
      !window.confirm(
        `Release this fulfillment hold and retry the normal safety checks?\n\n` +
          `Order: ${order.order_number ?? order.id}\n` +
          `Invoice: ${order.stripe_invoice_id ?? "unknown"}\n\n` +
          `This records your identity and reason. It does not bypass prescription, routing, or provider checks.`,
      )
    ) {
      return;
    }
    try {
      const response = await releaseHold.mutateAsync({
        orderId: order.id,
        idempotencyKey: releaseId,
        reason,
      });
      setResult(response);
    } catch (error) {
      setResult({
        pharmacy_order_id: null,
        status: "error",
        partner_key: null,
        partner_order_id: null,
        error: String((error as Error)?.message || error),
      });
    }
  }

  const who =
    order.patient_display_name || order.patient_email || order.patient_id;
  return (
    <Card padding="lg">
      <Card.Body>
        <div className="flex items-start justify-between gap-15 flex-wrap mb-15">
          <div className="flex flex-col gap-2 min-w-0 flex-1">
            <h3 className="type-title-md truncate">{who}</h3>
            {order.patient_email && order.patient_display_name && (
              <div className="type-label-sm text-ink-muted truncate">
                {order.patient_email}
              </div>
            )}
            {order.patient_healthie_id && (
              <a
                href={`https://secure.gethealthie.com/users/${order.patient_healthie_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="type-label-sm text-accent hover:underline w-fit"
              >
                Healthie profile ↗
              </a>
            )}
          </div>
          {!order.fulfillment_hold && (
            <Button
              size="sm"
              onClick={handleSubmit}
              loading={submit.isPending}
              disabled={result?.status === "submitted"}
            >
              {result?.status === "submitted" ? (
                "Submitted ✓"
              ) : isTph ? (
                <>
                  Submit to TPH <ArrowInline />
                </>
              ) : (
                <>
                  Submit to PPI <ArrowInline />
                </>
              )}
            </Button>
          )}
        </div>

        {order.fulfillment_hold && (
          <section
            className="mb-15 border border-status-warning bg-status-warning-subtle px-15 py-12"
            aria-labelledby={`fulfillment-hold-${order.id}`}
          >
            <h4
              id={`fulfillment-hold-${order.id}`}
              className="type-label-md uppercase tracking-tight text-status-warning"
            >
              Fulfillment held for review
            </h4>
            <p className="type-body-sm text-ink-secondary mt-4">
              {order.fulfillment_hold_reason ??
                "An earlier paid cycle is unresolved."}{" "}
              Review the earlier cycle before releasing this order.
            </p>
            <dl className="grid grid-cols-2 gap-x-20 gap-y-6 mt-10 type-body-sm">
              <Field label="Invoice" value={order.stripe_invoice_id || "—"} />
              <Field
                label="Blocking order"
                value={order.blocking_pharmacy_order_id || "—"}
              />
            </dl>
            <label
              htmlFor={`release-reason-${order.id}`}
              className="block type-label-sm mt-12"
            >
              Release reason
            </label>
            <textarea
              id={`release-reason-${order.id}`}
              value={releaseReason}
              onChange={(event) => setReleaseReason(event.target.value)}
              rows={3}
              maxLength={500}
              placeholder="What was reviewed, and why is this invoice safe to retry? Do not include patient details."
              className="w-full border border-line-default bg-surface px-10 py-8 type-body-sm mt-4"
            />
            <div className="flex items-center gap-10 mt-10 flex-wrap">
              <Button
                size="sm"
                variant="secondary"
                onClick={handleReleaseHold}
                loading={releaseHold.isPending}
                disabled={
                  releaseReason.trim().length < 8 || releaseAttemptRecorded
                }
              >
                {reconciliationPending
                  ? "Retry reconciliation with same release ID"
                  : releaseAttemptRecorded
                  ? "Release attempt recorded"
                  : "Release hold and retry"}
              </Button>
              <span className="type-body-sm text-ink-muted">
                Your identity and reason are recorded. Normal clinical and
                pharmacy checks still run.
              </span>
            </div>
          </section>
        )}

        <dl className="grid grid-cols-2 gap-x-20 gap-y-10">
          <Field label="Order #" value={order.order_number || "—"} />
          <Field label="Product" value={order.product_id || "—"} />
          <Field label="Partner" value={partnerName} />
          <Field
            label="Total"
            value={`$${(order.total_cents / 100).toFixed(2)}`}
          />
          <Field label="Created" value={created} />
          <Field
            label="Reason"
            value={order.awaiting_reason || "no_erx_on_file"}
          />
        </dl>

        {result && (
          <div
            role={
              result.status === "submitted" ||
              result.status === "still_awaiting" ||
              result.status === "held_for_review" ||
              result.status === "submitted_reconciliation_pending"
                ? "status"
                : "alert"
            }
            aria-live={result.status === "submitted" ? "polite" : "assertive"}
            className={`mt-15 px-15 py-10 type-body-sm ${
              result.status === "submitted"
                ? "bg-status-success-subtle text-status-success"
                : result.status === "still_awaiting" ||
                    result.status === "held_for_review" ||
                    result.status === "submitted_reconciliation_pending"
                  ? "bg-status-warning-subtle text-status-warning"
                  : "bg-status-danger-subtle text-status-danger"
            }`}
          >
            <div className="font-medium">Result: {result.status}</div>
            {result.partner_order_id && (
              <div className="mt-2">
                PPI order ID: <code>{result.partner_order_id}</code>
              </div>
            )}
            {result.error && <div className="mt-2">Error: {result.error}</div>}
            {result.status === "submitted_reconciliation_pending" && (
              <div className="mt-2">
                The pharmacy accepted the order. Retry with this button to
                reconcile the stored result; it reuses the same release ID and
                does not send another order.
              </div>
            )}
            {result.status === "still_awaiting" && (
              <div className="mt-2">
                The eRx is not yet on file in Healthie. Wait for the clinician
                to write it, then retry.
              </div>
            )}
          </div>
        )}

        <div className="mt-15 border-t border-line-subtle pt-12">
          <button
            type="button"
            onClick={() => setShowDetails((s) => !s)}
            className="type-label-sm uppercase tracking-tight text-accent hover:underline"
          >
            {showDetails
              ? "Hide prescription details ▴"
              : "Show prescription details ▾"}
          </button>
          {showDetails && (
            <div className="mt-12">
              {preview.isLoading ? (
                <p className="type-body-sm text-ink-muted">Loading preview…</p>
              ) : preview.isError ? (
                <p className="type-body-sm text-status-danger">
                  Failed to load preview:{" "}
                  {String((preview.error as Error)?.message || preview.error)}
                </p>
              ) : preview.data ? (
                <PrescriptionPreview preview={preview.data} />
              ) : null}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

function PrescriptionPreview({
  preview,
}: {
  preview: AwaitingOrderPreviewResponse;
}) {
  const isTph = preview.partner_key === "the_pharmacy_hub";
  const patient = preview.patient as {
    first_name?: string;
    last_name?: string;
    dob?: string;
    email?: string;
    phone?: string;
    gender?: string;
    shipping_address?: {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country?: string;
    };
  };
  const prescriber = preview.prescriber as {
    name?: string;
    npi?: string;
    phone?: string;
    address?: string;
  } | null;
  const addr = patient.shipping_address;
  const addrLine = addr
    ? [
        addr.line1,
        addr.line2,
        `${addr.city ?? ""}, ${addr.state ?? ""} ${addr.postal_code ?? ""}`.trim(),
      ]
        .filter(Boolean)
        .join(" · ")
    : "—";

  return (
    <div className="flex flex-col gap-15">
      {preview.notes.length > 0 && (
        <ul className=" border border-status-warning bg-status-warning-subtle px-12 py-10 type-body-sm text-status-warning list-disc pl-30">
          {preview.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      )}

      <section>
        <h4 className="type-label-md uppercase tracking-tight text-ink-muted mb-6">
          Patient
        </h4>
        <dl className="grid grid-cols-2 gap-x-20 gap-y-8 type-body-sm">
          <Field
            label="Name"
            value={
              `${patient.first_name ?? ""} ${patient.last_name ?? ""}`.trim() ||
              "—"
            }
          />
          <Field label="DOB" value={patient.dob || "—"} />
          <Field label="Email" value={patient.email || "—"} />
          <Field label="Phone" value={patient.phone || "—"} />
          <Field label="Gender" value={patient.gender || "—"} />
          <Field label="Ship to" value={addrLine} />
        </dl>
      </section>

      {prescriber && (
        <section>
          <h4 className="type-label-md uppercase tracking-tight text-ink-muted mb-6">
            Prescriber
          </h4>
          <dl className="grid grid-cols-2 gap-x-20 gap-y-8 type-body-sm">
            <Field label="Name" value={prescriber.name || "—"} />
            <Field label="NPI" value={prescriber.npi || "—"} />
            <Field label="Phone" value={prescriber.phone || "—"} />
            <Field label="Address" value={prescriber.address || "—"} />
          </dl>
        </section>
      )}

      <section>
        <h4 className="type-label-md uppercase tracking-tight text-ink-muted mb-6">
          Rx lines ({preview.lines.length})
        </h4>
        <div className="flex flex-col gap-15">
          {preview.lines.map((L, i) => (
            <LinePreview key={i} line={L} />
          ))}
        </div>
      </section>

      {isTph && (
        <section>
          <h4 className="type-label-md uppercase tracking-tight text-ink-muted mb-6">
            Rx PDF preview
          </h4>
          {preview.pdf_preview_base64 ? (
            <>
              <iframe
                title="Rx PDF preview"
                src={`data:application/pdf;base64,${preview.pdf_preview_base64}`}
                className="w-full h-[600px] border border-line-subtle bg-white"
              />
              <a
                href={`data:application/pdf;base64,${preview.pdf_preview_base64}`}
                download={preview.pdf_preview_filename || "rx-preview.pdf"}
                className="type-label-sm text-accent hover:underline mt-8 inline-block"
              >
                Download PDF ↓
              </a>
            </>
          ) : (
            <p className="type-body-sm text-ink-muted">
              PDF preview unavailable (check notes above).
            </p>
          )}
        </section>
      )}
    </div>
  );
}

function LinePreview({ line }: { line: AwaitingOrderLinePreview }) {
  const meta = (line.pharmacy_product || {}) as Record<string, unknown>;
  const get = (k: string): string => {
    const v = meta[k];
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "boolean") return v ? "yes" : "no";
    return String(v);
  };
  return (
    <div className=" border border-line-subtle p-12">
      <div className="flex items-baseline justify-between gap-10 flex-wrap mb-8">
        <div className="type-title-sm">{get("medication_name")}</div>
        <div className="type-label-sm text-ink-muted">
          {line.product_id} · qty {line.quantity}
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-x-20 gap-y-6 type-body-sm">
        <Field label="Stripe product name" value={line.stripe_name || "—"} />
        <Field label="Partner row" value={get("partner_key")} />
        <Field label="medication_id" value={get("medication_id")} />
        <Field label="CSN" value={get("csn")} />
        <Field label="NDC" value={get("ndc")} />
        <Field label="default_quantity" value={get("default_quantity")} />
        <Field label="quantity_unit" value={get("quantity_unit")} />
        <Field label="lot_size" value={get("lot_size")} />
        <Field label="refills_authorized" value={get("refills_authorized")} />
        <Field label="ship_refrigerated" value={get("ship_refrigerated")} />
        <Field label="carrier" value={get("carrier")} />
        <Field label="shipping_method" value={get("shipping_method")} />
        <Field
          label="prescriber"
          value={`${get("doctor_first_name")} ${get("doctor_last_name")} (NPI ${get("doctor_npi")})`.replace(
            /— /g,
            "",
          )}
        />
      </dl>
      <div className="mt-10">
        <div className="type-label-sm uppercase tracking-tight text-ink-muted mb-4">
          Sig / directions
        </div>
        <div className="type-body-sm whitespace-pre-wrap bg-surface-subtle px-12 py-8">
          {get("instructions")}
        </div>
      </div>
      {line.transmit_payload && (
        <details className="mt-10">
          <summary className="type-label-sm uppercase tracking-tight text-ink-muted cursor-pointer">
            Transmit payload (raw)
          </summary>
          <pre className="mt-6 text-[1.1rem] leading-[1.4] overflow-x-auto bg-surface-subtle px-12 py-10">
            {JSON.stringify(line.transmit_payload, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="type-label-sm text-ink-muted uppercase tracking-tight">
        {label}
      </dt>
      <dd className="type-body-md break-words">{value}</dd>
    </div>
  );
}

function filterOrders(
  orders: AwaitingOrderRow[],
  query: string,
): AwaitingOrderRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return orders;
  return orders.filter((o) => {
    const haystack = [
      o.patient_display_name,
      o.patient_email,
      o.product_id,
      o.order_number,
      o.patient_id,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}
